import { motion } from "framer-motion";

const GROUPS = [
  {
    title: "Leadership",
    members: [
      {
        name: "Luke Wilson MNZM", role: "Director & Founder",
        hometown: "Wairarapa",
        outside: "Diving, fishing, music and hosting family and friends.",
        why: "Over 30 years in the industry, from $4.5M commercial developments to small renovations. Former New Zealand Army engineer with 5 deployments including Antarctica, East Timor and Afghanistan, where he designed and built defensive structures for forward operating bases. Awarded the MNZM (Member of the New Zealand Order of Merit) for his service. Passionate about giving back to the community.",
      },
      {
        name: "Brooke Cross", role: "Administration & Health and Safety Representative",
        hometown: "Wairarapa",
        outside: "A keen eye for detail and a passion for organisation — on and off the clock.",
        why: "Brooke keeps LW&Co running seamlessly behind the scenes: schedules, correspondence and financial administration, plus the team's wellbeing through rigorous health and safety compliance — risk assessments, safety policies and staff training.",
      },
    ],
  },
  {
    title: "Lead Builders",
    members: [
      {
        name: "George Liu", role: "Lead Builder · Rangitane, Ngati Kahungunu",
        hometown: "Martinborough",
        outside: "Fishing the Wairarapa coastline and spending time with whanau.",
        why: "Nearly three decades in residential and commercial construction, beginning with his carpentry apprenticeship through Hau Ariki Marae in 1996. Notable projects include Wollongong University (NSW), the American Express building at Darling Harbour and multistorey apartments in Glebe. Values transparency and clear communication.",
      },
      {
        name: "Joe Feast", role: "Lead Builder · Ngāti Kahungunu, Ngāi Tūmapūhia-a-Rangi",
        hometown: "Wairarapa — strong ties to Ōkautete Marae",
        outside: "Plays rugby for East Coast Rugby Club and loves time with his wife and two kids.",
        why: "A seasoned carpenter with over 15 years experience. Joe began his apprenticeship alongside his father Peter Feast and spent nine years with Westwood Construction on new builds across the Wairarapa including Martinborough Estate, Westwood Greytown and Solway Estate.",
      },
    ],
  },
  {
    title: "Qualified Builders",
    members: [
      {
        name: "Caleb Nunn", role: "Qualified Builder · Ngati Raukawa",
        background: "2 years as a qualified builder with LW&Co.",
      },
      {
        name: "Hamish Finlayson", role: "Qualified Builder",
        background: "2 years as a qualified builder with LW&Co.",
      },
      {
        name: "Shaiane", role: "Qualified Builder",
        hometown: "Born and bred Masterton",
        outside: "Golf, BBQs with a cold one in summer, and time with family.",
        why: "Wanted a new challenge. Loves the variety, the problem-solving, and that satisfying feeling when a tough job comes together.",
      },
      {
        name: "Phillip Tuoro", role: "Qualified Builder · Cook Island",
        background: "Newly qualified — recently completed his BCITO qualification. Congratulations Phil! 3 years with LW&Co.",
      },
    ],
  },
  {
    title: "Apprentices",
    members: [
      {
        name: "Jacob Day", role: "Apprentice · Nga Tahu",
        background: "1st year BCITO apprentice.",
      },
      {
        name: "Jacob Awa", role: "New Apprentice",
        hometown: "Born and raised in Masterton",
        outside: "Loves sport and family.",
        why: "Got into building because he's always loved it and enjoys the crew.",
      },
      {
        name: "Taine Jones", role: "New Apprentice",
        hometown: "Masterton born",
        outside: "Into hunting and diving.",
        why: "Got into building for the outdoors, the variety and the satisfaction of seeing what he's built at the end of the day.",
      },
    ],
  },
  {
    title: "Design & Media",
    members: [
      { name: "Emma Higgins", role: "Design & Media" },
    ],
  },
];

const initials = (name) =>
  name.replace(/ MNZM$/, "").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
    className={className}>
    {children}
  </motion.div>
);

function ProfileRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="pt-4">
      <div className="text-[9px] font-bold uppercase tracking-[0.3em] text-gold/80">{label}</div>
      <p className="mt-1.5 text-sm text-white/60 leading-relaxed">{value}</p>
    </div>
  );
}

function MemberCard({ member, delay, testid }) {
  return (
    <Reveal delay={delay} className="h-full">
      <article data-testid={testid}
        className="group h-full border border-white/10 bg-white/[0.03] p-8 hover:border-gold/60 hover:-translate-y-1 transition-all duration-300 flex flex-col">
        <div className="w-14 h-14 border border-gold/50 bg-gold/5 flex items-center justify-center">
          <span className="font-display font-semibold text-lg text-gold tracking-wide">{initials(member.name)}</span>
        </div>
        <h3 className="font-display font-semibold uppercase tracking-tight text-xl sm:text-2xl mt-6 group-hover:text-gold transition-colors duration-300">
          {member.name}
        </h3>
        <p className="mt-2 text-[10px] font-bold uppercase tracking-[0.25em] text-gold">{member.role}</p>
        <div className="mt-5 border-t border-white/10 divide-y divide-white/[0.06] flex-1">
          <ProfileRow label="Hometown" value={member.hometown} />
          <ProfileRow label="Outside of work" value={member.outside} />
          <ProfileRow label="Why building" value={member.why} />
          <ProfileRow label="Background" value={member.background} />
        </div>
      </article>
    </Reveal>
  );
}

export default function Team() {
  return (
    <main data-testid="team-page" className="bg-black text-white">
      {/* HERO */}
      <section className="pt-56 pb-20 sm:pb-24 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(circle at 15% 0%, rgba(201,169,110,0.12) 0%, transparent 55%)" }} />
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            className="text-[11px] font-bold uppercase tracking-[0.3em] text-gold mb-6">The crew</motion.p>
          <div className="overflow-hidden">
            <motion.h1 initial={{ y: "100%" }} animate={{ y: 0 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
              className="font-display font-semibold uppercase leading-[0.95] tracking-tight text-5xl sm:text-7xl lg:text-8xl"
              data-testid="team-headline">
              Meet the Team
            </motion.h1>
          </div>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}
            className="mt-8 max-w-xl text-base sm:text-lg text-white/65 leading-relaxed" data-testid="team-subtext">
            The people behind every build — experienced, passionate and proudly Wairarapa.
          </motion.p>
        </div>
      </section>

      {/* GROUPS */}
      {GROUPS.map((group, gi) => (
        <section key={group.title} className="py-16 sm:py-20 border-t border-white/10" data-testid={`team-group-${group.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
            <Reveal className="flex items-baseline gap-4 mb-10">
              <span className="text-[11px] font-bold tracking-[0.3em] text-gold">{String(gi + 1).padStart(2, "0")}</span>
              <h2 className="font-display font-semibold uppercase tracking-tight text-3xl sm:text-4xl">{group.title}</h2>
            </Reveal>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {group.members.map((m, i) => (
                <MemberCard key={m.name} member={m} delay={(i % 3) * 0.08}
                  testid={`team-card-${m.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />
              ))}
            </div>
          </div>
        </section>
      ))}
    </main>
  );
}
